import { inject, injectable } from 'inversify';
import type { CancellationPolicyRequest, CancellationPolicyResponse } from '@bookingkit/api-v4/dist/models';
import { BookingKitApiFactoryInterface } from '@/models/Factories/BookingKitApiFactoryInterface';
import { BOOKINGKIT_API_FACTORY, SUPPLIER_STATE_SERVICE } from '@/bootstrap/ServiceProviders';
import { UseSupplierInterface } from '@/state/supplier';
import { CancellationPoliciesServiceError } from './Errors/CancellationPoliciesServiceError';

@injectable()
export class CancellationPoliciesService {
    private connection: ReturnType<BookingKitApiFactoryInterface['getConnection']>;

    private supplierId: string;

    constructor(
        @inject(BOOKINGKIT_API_FACTORY) factory: BookingKitApiFactoryInterface,
        @inject(SUPPLIER_STATE_SERVICE) useSupplierState: () => UseSupplierInterface,
    ) {
        const { supplier } = useSupplierState();
        if (!supplier.value?.id) {
            throw CancellationPoliciesServiceError.fromMissingSupplier();
        }
        this.supplierId = supplier.value.id;
        this.connection = factory.getConnection();
        this.listPolicies = this.listPolicies.bind(this);
        this.createPolicy = this.createPolicy.bind(this);
    }

    public async listPolicies(): Promise<CancellationPolicyResponse[]> {
        const { supplierId } = this;
        const { data } = await this.connection.cancellationPoliciesList({ vendorId: supplierId });
        if (!Array.isArray(data?.data)) {
            throw CancellationPoliciesServiceError.fromInvalidProperty('data.data', 'array', data?.data);
        }
        return data.data;
    }

    public async createPolicy(policy: CancellationPolicyRequest): Promise<CancellationPolicyResponse> {
        const { supplierId } = this;
        const { data } = await this.connection.cancellationPoliciesCreate({
            vendorId: supplierId,
            cancellationPolicyRequest: policy,
        });

        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            throw CancellationPoliciesServiceError.fromInvalidProperty('data', 'object', data);
        }
        return data;
    }
}

export default CancellationPoliciesService;
